import React from 'react';
import { formatPercentage } from '../../../utils/numberUtils';

const HELPER = {
  location: 'Set the location budget to track utilization.',
  organization: 'Set annual budgets on locations to track utilization.'
};

/**
 * Renders utilization % when an annual budget is set; otherwise "Unbudgeted" + helper text.
 *
 * @param {object} [props.loc] - Shortcut: { allocated_budget, budget_utilization_percent }
 * @param {number} [props.allocatedBudget] - Overrides loc.allocated_budget
 * @param {number|null|undefined} [props.budgetUtilizationPercent] - Overrides loc field; null/undefined treated as unset when paired with zero budget logic is handled via allocatedBudget only
 * @param {'location'|'organization'} [props.scope='location']
 * @param {boolean} [props.compact] - Tighter layout (tables)
 * @param {boolean} [props.large] - Headline sizing for summary cards (governance strip)
 * @param {boolean} [props.budgetOverrun] - When large + budget set, picks red vs blue tone
 */
export default function BudgetUtilizationDisplay ({
  loc,
  allocatedBudget: allocatedBudgetProp,
  budgetUtilizationPercent: pctProp,
  scope = 'location',
  compact = false,
  large = false,
  budgetOverrun = false
}) {
  const allocatedBudget =
    allocatedBudgetProp !== undefined && allocatedBudgetProp !== null
      ? allocatedBudgetProp
      : loc?.allocated_budget;
  const hasBudget = (allocatedBudget ?? 0) > 0;
  const pct =
    pctProp !== undefined && pctProp !== null
      ? pctProp
      : loc?.budget_utilization_percent !== undefined && loc?.budget_utilization_percent !== null
        ? loc.budget_utilization_percent
        : hasBudget
          ? 0
          : null;
  const helperText = HELPER[scope] || HELPER.location;

  if (!hasBudget) {
    if (compact) {
      return (
        <span
          style={{
            fontWeight: '600',
            fontSize: '0.72rem',
            color: '#64748b',
          }}
          title={helperText}
        >
          Unbudgeted
        </span>
      );
    }
    const noteStyle = {
      fontSize: large ? '0.72rem' : '0.58rem',
      color: '#94a3b8',
      lineHeight: '1.25',
      maxWidth: large ? '14rem' : '9rem',
      textAlign: large ? 'left' : 'center'
    };
    const badgeFont = large ? '1.1rem' : '0.72rem';
    const align = large ? 'flex-start' : 'center';
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: align,
          gap: large ? '0.35rem' : '0.2rem',
          textAlign: large ? 'left' : 'center'
        }}
      >
        <span
          style={{
            fontWeight: '700',
            fontSize: badgeFont,
            color: '#475569',
            backgroundColor: '#f1f5f9',
            padding: '0.25rem 0.45rem',
            borderRadius: '4px'
          }}
          title={helperText}
        >
          Unbudgeted
        </span>
        <span style={noteStyle}>{helperText}</span>
      </div>
    );
  }

  const p = pct ?? 0;

  if (large) {
    const color = budgetOverrun ? '#d32f2f' : '#1976d2';
    return (
      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color }}>
        {formatPercentage(p)}
      </div>
    );
  }

  if (compact) {
    return (
      <span style={{ fontWeight: '600', color: p > 90 ? '#d32f2f' : p > 75 ? '#f57c00' : '#4caf50' }}>
        {formatPercentage(p)}
      </span>
    );
  }

  return (
    <span
      style={{
        fontWeight: '700',
        color: p > 90 ? '#d32f2f' : p > 75 ? '#f57c00' : '#4caf50',
        backgroundColor: p > 90 ? '#ffebee' : p > 75 ? '#fff3e0' : '#e8f5e9',
        padding: '0.3rem 0.5rem',
        borderRadius: '4px',
        display: 'inline-block',
        minWidth: '3rem'
      }}
    >
      {formatPercentage(p)}
    </span>
  );
}
